import React from 'react';
import { Shape } from 'react-konva';
import { getIngredientsByIngredientStockId, Round } from 'utils/Global';
import {
  generateWellTooltipText,
  getDropVolumeText,
  getDropScreenNamesAndVolsByDropNumber,
  getDropProteinVolumesByDropNumber,
  getDropSeedVolumesByDropNumber,
  getFragmentScreenNameByDropNumber,
  getSeedNamesByDropNumber,
  getWellScreenLayerName,
} from './utils';
import { DrawingLayer } from 'business/models/DrawingLayer';
import { WellIng } from 'components/Experiment/constants';
import { EPrecisionType, PrecisionFormatter } from '../constants';

const MAX_NUM_INGS = 6;
const MAX_NUM_INGS_WITH_SCORES = 5;

// Helper function to truncate text
const truncateText = (context, text, maxWidth) => {
  const ellipsis = '...';
  const ellipsisWidth = context.measureText(ellipsis).width;
  let truncatedText = text;
  let width = context.measureText(text).width;
  while (width > maxWidth - ellipsisWidth) {
    truncatedText = truncatedText.slice(0, -1);
    width = context.measureText(truncatedText).width;
  }
  if (text !== truncatedText) {
    truncatedText += ellipsis;
  }
  return truncatedText;
};

const drawHeader = (
  context,
  x,
  y,
  w,
  fontSize,
  headerTexts,
  headerFillColor,
  textFillColor,
  lineSpacing,
) => {
  const rectHeight = fontSize * 1.5;
  const columnWidth = w / 4;
  const rectY = y + fontSize * lineSpacing;
  const textOffset = (rectHeight - fontSize) / 2;
  const textY = rectY + textOffset + rectHeight;
  // Draw header background
  context.fillStyle = headerFillColor;
  context.fillRect(x, rectY + fontSize / 2, w, rectHeight);

  // Draw header text
  context.fillStyle = textFillColor;
  context.font = `${fontSize}px Roboto`;

  let cellStart = x;
  headerTexts.forEach((text, index) => {
    const textWidth = context.measureText(text).width;
    const textX = cellStart + (columnWidth - textWidth) / 2;
    context.fillText(text, textX, textY);
    cellStart += columnWidth;
  });
};

const drawIngredientsData = (
  context,
  x,
  y,
  w,
  ingName,
  fontSize,
  customCanvas,
  lineSpacing,
  isHeader,
  isEven,
) => {
  const truncatedIngName = truncateText(customCanvas, ingName, w - 2 * fontSize);
  const rectHeight = fontSize * 1.5;

  // Draw horizontal line at the start of the ingredients cell
  if (isHeader) {
    const startY = y + fontSize * (lineSpacing - 1);
    context.beginPath();
    context.strokeStyle = '#888888';
    context.lineWidth = 0.2;
    context.moveTo(x, startY);
    context.lineTo(x + w, startY);
    context.stroke();
  }
  if (isEven) {
    context.fillStyle = '#CACACA'; // Set the background color for even rows
    context.fillRect(x, y + fontSize * (lineSpacing - 1), w, rectHeight); // Draw the background rectangle with correct height
  }
  context.fillStyle = 'black'; // Text color
  context.fillText(truncatedIngName, x + 1, y + fontSize * lineSpacing);

  // Draw horizontal divider between ingredient name and values
  const endY = y + fontSize * (lineSpacing - 1) + rectHeight;
  context.beginPath();
  context.strokeStyle = '#888888';
  context.lineWidth = 0.2;
  context.moveTo(x, endY);
  context.lineTo(x + w, endY);
  context.stroke();
};

const drawValuesRow = (
  context,
  x,
  y,
  w,
  fontSize,
  values,
  lineSpacing,
  isEven,
  isLastIngredient,
) => {
  const rectHeight = fontSize * 1.5;
  const columnWidth = w / 4; // Divide width equally for each value
  const rowY = y + fontSize * (lineSpacing - 0.5);

  if (isEven) {
    context.fillStyle = '#CACACA'; // Set the background color for even rows

    // Draw a background rectangle spanning the entire row for the values
    context.fillRect(x, rowY, w, rectHeight); // Adjust width to prevent overflow
  }

  // Draw vertical column dividers
  let dividerX = w / 4;
  for (let i = 0; i < values.length; i++) {
    context.beginPath();
    context.strokeStyle = '#888888';
    context.lineWidth = 0.2;
    context.moveTo(dividerX, rowY);
    context.lineTo(dividerX, rowY + rectHeight);
    context.stroke();
    dividerX += w / 4;
  }

  // Draw each value in the row
  context.fillStyle = 'black';
  let cellStart = x;
  values.forEach((text, index) => {
    const textWidth = context.measureText(text).width;
    const textX = cellStart + (columnWidth - textWidth) / 2;
    context.fillText(text, textX, y + fontSize * (lineSpacing - 1) + rectHeight); // Draw the text
    cellStart += columnWidth;
  });

  if (isLastIngredient) {
    context.beginPath();
    context.strokeStyle = '#888888';
    context.lineWidth = 0.2;
    context.moveTo(x, rowY + rectHeight);
    context.lineTo(w, rowY + rectHeight);
    context.stroke();
  }
};

const headerTexts = ['Conc', 'L vol', 'H vol', 'pH'];
// Function to create sceneFunc and hitFunc for the custom shape
const createWellIngredientsShape = (
  x,
  y,
  w,
  h,
  wellData,
  dropNumber,
  combinedIngredients,
  dropScreenNamesAndVolumes,
  dropProteinVolumes,
  dropSeedVolumes,
  hasScores: boolean,
  formatWithPrecision: PrecisionFormatter,
) => {
  const sceneFunc = (context, shape) => {
    let lineSpacing = 0.9;
    if (hasScores) {
      lineSpacing *= 4;
    }
    const fontSize = h / 25;
    const drop = wellData.dropList?.find((d) => d.dropNumber === dropNumber);
    const customCanvas = document.createElement('canvas').getContext('2d');
    customCanvas.font = `${fontSize}px Roboto`;

    context.clearRect(x, y, w, h);

    // Plate ID and Drop Information
    context.font = `bold ${fontSize}px Roboto`;
    context.fillText(
      `Plate ID ${wellData.plateId}, ${wellData.rowLetter}${wellData.columnNumber}${
        drop?.dropNumber ? `-${drop.dropNumber}` : ''
      }`,
      x + 1,
      y + fontSize * lineSpacing++ + 1,
    );

    let displayText = combinedIngredients;

    // Roughly estimate max characters that fit in one line
    const maxCharsPerLine = Math.floor(w / (fontSize * 0.6));
    if (displayText.length > maxCharsPerLine * 2) {
      displayText = displayText.slice(0, maxCharsPerLine * 2 - 3) + '...';
    }
    // Split into two lines max
    const firstLine = displayText.slice(0, maxCharsPerLine);
    const secondLine = displayText.slice(maxCharsPerLine);

    if (secondLine) {
      lineSpacing -= 0.2;
    }

    context.font = `${fontSize}px Roboto`;
    context.fillText(lineSpacing++);
    context.fillText(firstLine, x + 1, y + fontSize * lineSpacing++);
    if (secondLine) {
      context.fillText(secondLine, x + 1, y + fontSize * lineSpacing++);
    }
    // Sacrifice a little whitespace to give more space at the end, especially for unscored wells
    lineSpacing -= secondLine ? 0.8 : 0.2;
    context.fillText(lineSpacing++);

    if (drop) {
      const dropVolumeText = getDropVolumeText(
        drop,
        dropScreenNamesAndVolumes,
        dropProteinVolumes,
        dropSeedVolumes,
      );
      if (dropVolumeText) {
        const limit = maxCharsPerLine + 10;
        const finalText =
          dropVolumeText.length > limit
            ? dropVolumeText.slice(0, limit) + '...'
            : dropVolumeText;

        context.fillText(finalText, x + 1, y + fontSize * lineSpacing++);
      }
    }
    context.fillText(
      `Well: ${Round(wellData.wellVolume)}µL / ${formatWithPrecision(
        wellData.wellWaterVolume,
        EPrecisionType.Volume,
      )}µL H2O  `,
      x + 1,
      y + fontSize * lineSpacing,
    );

    // Add slight pad for wells with score, to match the non-scored one
    if (hasScores) {
      lineSpacing += 0.3;
    }

    if (secondLine) {
      lineSpacing -= 0.2;
    }

    // Draw header
    drawHeader(
      context,
      x,
      y,
      w,
      fontSize,
      headerTexts,
      '#5D5D5D',
      'white',
      lineSpacing++,
    );

    // Draw ingredients
    lineSpacing++;
    const totalIngredients = wellData.wellLayerList.reduce(
      (sum, wellLayer) => sum + wellLayer.wellLayerIngredientList.length,
      0,
    );
    let ingredientsTableCounter = 0;
    const maxIngs = hasScores ? MAX_NUM_INGS_WITH_SCORES : MAX_NUM_INGS;
    const maxIngsNum = Math.min(totalIngredients, maxIngs);
    wellData.wellLayerList.forEach((wellLayer) => {
      wellLayer.wellLayerIngredientList.forEach((wellLayerIngredient) => {
        ingredientsTableCounter++;
        if (ingredientsTableCounter > maxIngsNum) {
          return;
        }

        const { stock, ingredient } = getIngredientsByIngredientStockId(
          wellLayerIngredient.ingredientStockId,
        );
        const ingName = ingredient?.longName;
        const isEven = (ingredientsTableCounter + 1) % 2 !== 0;
        const isLastIngredient = ingredientsTableCounter === maxIngsNum;
        const values = [
          `${formatWithPrecision(
            wellLayerIngredient.concentration,
            EPrecisionType.Concentration,
          )}${stock?.concentrationUnits}`,
          `${formatWithPrecision(
            wellLayerIngredient.isBuffer
              ? wellLayerIngredient?.lowPHVolume
              : wellLayerIngredient?.volume,
            EPrecisionType.Volume,
          )}µL`,
          wellLayerIngredient.highPHVolume
            ? `${formatWithPrecision(
                wellLayerIngredient.highPHVolume,
                EPrecisionType.Volume,
              )}µL`
            : '',
          wellLayerIngredient.pH
            ? `${formatWithPrecision(wellLayerIngredient.pH, EPrecisionType.PH)}pH`
            : '',
        ];
        context.fillStyle = 'black';
        drawIngredientsData(
          context,
          x,
          y + fontSize,
          w,
          ingName,
          fontSize,
          customCanvas,
          lineSpacing++,
          true,
          isEven,
        );
        lineSpacing++;
        drawValuesRow(
          context,
          x,
          y,
          w,
          fontSize,
          values,
          lineSpacing++,
          isEven,
          isLastIngredient,
        );
      });
    });

    // Draw border
    context.strokeStyle = 'black';
    context.lineWidth = Math.min(1, fontSize);
    context.strokeRect(x, y, w, h + 2);

    context.fillStrokeShape(shape);
  };

  const hitFunc = (context, shape) => {
    context.beginPath();
    context.rect(x, y, w, h);
    context.closePath();
    context.fillStrokeShape(shape);
  };

  return { sceneFunc, hitFunc };
};

export interface WellIngredientsProps {
  x: number;
  y: number;
  w: number;
  h: number;
  wellData: WellIng;
  dropNumber: number;
  layers?: DrawingLayer[];
  wellPositionDetails?: { x: number; y: number; colPos: number; rowPos: number };
  hasScores: boolean;
  formatWithPrecision: PrecisionFormatter;
}

const IngredientDetailRenderer = (props: WellIngredientsProps) => {
  const {
    x,
    y,
    w,
    h,
    wellData,
    dropNumber,
    layers,
    wellPositionDetails,
    hasScores,
    formatWithPrecision,
  } = props;
  const seedName = getSeedNamesByDropNumber(wellData, dropNumber);
  const screenLayerName = getWellScreenLayerName(layers, wellData);
  const fragmentScreenName = getFragmentScreenNameByDropNumber(
    layers,
    wellData,
    dropNumber,
  );
  const dropScreenNamesAndVols = getDropScreenNamesAndVolsByDropNumber(
    layers,
    wellData,
    dropNumber,
  );
  const dropProteinVolumes = getDropProteinVolumesByDropNumber(
    layers,
    wellData,
    dropNumber,
    wellPositionDetails,
  );
  const proteinNames =
    dropProteinVolumes
      .map((item) => item.proteinName)
      .filter((name): name is string => name !== undefined)
      .join(' + ') ?? '';
  const dropSeedVolumes = getDropSeedVolumesByDropNumber(
    layers,
    wellData,
    dropNumber,
    wellPositionDetails,
  );

  const dropScreenNames = dropScreenNamesAndVols
    ?.map((x) => x.name)
    .filter((name): name is string => Boolean(name))
    .join(' + ');

  const combinedIngredients = [
    screenLayerName,
    proteinNames,
    seedName,
    fragmentScreenName,
    dropScreenNames,
  ]
    .filter(Boolean)
    .join(' + ');

  const { sceneFunc, hitFunc } = createWellIngredientsShape(
    0,
    0,
    w,
    h,
    wellData,
    dropNumber,
    combinedIngredients,
    dropScreenNamesAndVols,
    dropProteinVolumes,
    dropSeedVolumes,
    hasScores,
    formatWithPrecision,
  );

  return (
    <Shape
      id="render-plate-wellingview-ingdetail-render"
      sceneFunc={sceneFunc}
      hitFunc={hitFunc}
      x={x}
      y={y}
      w={w}
      h={h}
      tooltipInfo={generateWellTooltipText(wellData, formatWithPrecision)}
      wellPositionDetails={wellPositionDetails}
    />
  );
};

export default IngredientDetailRenderer;
